import React, { useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Table, InputNumber, Typography, Tooltip, Form, Input,
} from 'antd';
import {
  PRICE_FIELDS,
  PRICE_FIELD_KEYS,
  priceFieldGreyOut,
  calculateProgressivePrice,
  isKegSku,
} from '../../../../../util/pricing-fields';

const { Text } = Typography;

/**
 * Off Prem with cascade to Progressive (same rules as Pricing Manager).
 */
function SkuPriceOffInput({
  value,
  onChange,
  fieldName,
  form,
  sku,
  getProgressiveAuto,
  setProgressiveAuto,
  disabled,
  hasChange,
  oldVal,
}) {
  const handleChange = (v) => {
    onChange(v);
    const row = form.getFieldValue(['skus', fieldName]) || {};
    if (isKegSku(sku) || isKegSku(row)) return;
    if (v == null || v === '') {
      setProgressiveAuto(false);
      return;
    }
    const rowWithOff = { ...row, priceOff: v };
    const sug = calculateProgressivePrice(v, rowWithOff);
    if (sug == null) return;
    const prevProg = row.priceProgressive;
    const wasAuto = getProgressiveAuto();
    if (wasAuto || prevProg == null || prevProg === '') {
      form.setFieldValue(['skus', fieldName, 'priceProgressive'], sug);
      setProgressiveAuto(true);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 11, lineHeight: 1.3, marginBottom: 2 }}>
        <Text
          type="secondary"
          style={{
            textDecoration: hasChange ? 'line-through' : 'none',
          }}
        >
          {oldVal != null && oldVal !== '' ? oldVal : '-'}
        </Text>
      </div>
      <InputNumber
        value={value}
        onChange={handleChange}
        size="small"
        disabled={disabled}
        step={0.5}
        min={0}
        controls={false}
        style={{
          width: '100%',
          minWidth: 52,
          borderColor: hasChange ? '#1890ff' : undefined,
          backgroundColor: hasChange ? '#e6f7ff' : undefined,
        }}
      />
    </div>
  );
}

SkuPriceOffInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  fieldName: PropTypes.number.isRequired,
  form: PropTypes.shape({
    getFieldValue: PropTypes.func,
    setFieldValue: PropTypes.func,
  }).isRequired,
  sku: PropTypes.shape({
    containerType: PropTypes.string,
    container_type: PropTypes.string,
    caseType: PropTypes.string,
    case_type: PropTypes.string,
    packType: PropTypes.string,
    pack_type: PropTypes.string,
  }),
  getProgressiveAuto: PropTypes.func.isRequired,
  setProgressiveAuto: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hasChange: PropTypes.bool,
  oldVal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SkuPriceOffInput.defaultProps = {
  value: undefined,
  sku: {},
  disabled: false,
  hasChange: false,
  oldVal: undefined,
};

function SkuPriceProgressiveInput({
  value,
  onChange,
  setProgressiveAuto,
  disabled,
  hasChange,
  oldVal,
}) {
  return (
    <div>
      <div style={{ fontSize: 11, lineHeight: 1.3, marginBottom: 2 }}>
        <Text
          type="secondary"
          style={{
            textDecoration: hasChange ? 'line-through' : 'none',
          }}
        >
          {oldVal != null && oldVal !== '' ? oldVal : '-'}
        </Text>
      </div>
      <InputNumber
        value={value}
        onChange={(v) => {
          setProgressiveAuto(false);
          onChange(v);
        }}
        size="small"
        disabled={disabled}
        step={0.5}
        min={0}
        controls={false}
        style={{
          width: '100%',
          minWidth: 52,
          borderColor: hasChange ? '#1890ff' : undefined,
          backgroundColor: hasChange ? '#e6f7ff' : undefined,
        }}
      />
    </div>
  );
}

SkuPriceProgressiveInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  setProgressiveAuto: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hasChange: PropTypes.bool,
  oldVal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SkuPriceProgressiveInput.defaultProps = {
  value: undefined,
  disabled: false,
  hasChange: false,
  oldVal: undefined,
};

/**
 * Compact single-row pricing table for the SKU editor.
 * Dynamically hides columns that don't apply to the current SKU type
 * and shows old (saved) vs new (form) prices side-by-side.
 */
const SkuPricingTable = ({
  fieldName,
  sku,
  savedPrices,
  disabled,
}) => {
  const form = Form.useFormInstance();
  const watchedSku = Form.useWatch(['skus', fieldName]) || {};
  const progressiveAutoRef = useRef(false);
  const getProgressiveAuto = useCallback(() => progressiveAutoRef.current, []);
  const setProgressiveAuto = useCallback((v) => {
    progressiveAutoRef.current = v;
  }, []);

  const relevantFields = useMemo(
    () => PRICE_FIELDS.filter((pf) => !priceFieldGreyOut(sku, pf.key)),
    [sku],
  );

  const hiddenFieldKeys = useMemo(
    () => PRICE_FIELD_KEYS.filter((k) => priceFieldGreyOut(sku, k)),
    [sku],
  );

  const columns = useMemo(
    () => relevantFields.map((pf) => ({
      title: (
        <Tooltip title={pf.tooltip}>
          <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{pf.label}</span>
        </Tooltip>
      ),
      dataIndex: pf.key,
      key: pf.key,
      width: 120,
      render: (_, record) => {
        const oldVal = savedPrices?.[pf.key];
        const currentVal = record[pf.key];
        const hasChange = currentVal != null
          && currentVal !== ''
          && String(currentVal) !== String(oldVal ?? '');

        if (pf.key === 'priceOff') {
          return (
            <Form.Item
              name={[fieldName, pf.key]}
              noStyle
            >
              <SkuPriceOffInput
                fieldName={fieldName}
                form={form}
                sku={sku}
                getProgressiveAuto={getProgressiveAuto}
                setProgressiveAuto={setProgressiveAuto}
                disabled={disabled}
                hasChange={hasChange}
                oldVal={oldVal}
              />
            </Form.Item>
          );
        }

        if (pf.key === 'priceProgressive') {
          return (
            <Form.Item
              name={[fieldName, pf.key]}
              noStyle
            >
              <SkuPriceProgressiveInput
                setProgressiveAuto={setProgressiveAuto}
                disabled={disabled}
                hasChange={hasChange}
                oldVal={oldVal}
              />
            </Form.Item>
          );
        }

        return (
          <div>
            <div style={{ fontSize: 11, lineHeight: 1.3, marginBottom: 2 }}>
              <Text
                type="secondary"
                style={{
                  textDecoration: hasChange ? 'line-through' : 'none',
                }}
              >
                {oldVal != null && oldVal !== '' ? oldVal : '-'}
              </Text>
            </div>
            <Form.Item
              name={[fieldName, pf.key]}
              noStyle
            >
              <InputNumber
                size="small"
                disabled={disabled}
                step={0.5}
                min={0}
                controls={false}
                style={{
                  width: '100%',
                  minWidth: 52,
                  borderColor: hasChange ? '#1890ff' : undefined,
                  backgroundColor: hasChange ? '#e6f7ff' : undefined,
                }}
              />
            </Form.Item>
          </div>
        );
      },
    })),
    [
      relevantFields, savedPrices, fieldName, disabled, form, sku,
      getProgressiveAuto, setProgressiveAuto,
    ],
  );

  const dataSource = useMemo(
    () => [{ key: 'sku-row', ...watchedSku }],
    [watchedSku],
  );

  return (
    <>
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="key"
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
          bordered
          showHeader
        />
      </div>
      {/* Hidden Form.Items for excluded price fields to preserve form values */}
      {hiddenFieldKeys.map((k) => (
        <Form.Item key={k} name={[fieldName, k]} hidden>
          <Input />
        </Form.Item>
      ))}
    </>
  );
};

SkuPricingTable.propTypes = {
  fieldName: PropTypes.number.isRequired,
  sku: PropTypes.shape({
    containerType: PropTypes.string,
    container_type: PropTypes.string,
    packType: PropTypes.string,
    pack_type: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  savedPrices: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  disabled: PropTypes.bool,
};

SkuPricingTable.defaultProps = {
  sku: {},
  savedPrices: {},
  disabled: false,
};

export default SkuPricingTable;
